import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popper,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Editor } from '@tinymce/tinymce-react';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import CalendarFormIcon from '../../../../packages/common/shared-ui/src/icons/CalendarFormIcon';
import CheckboxIcon from '../../../../packages/common/shared-ui/src/icons/CheckboxIcon';
import CheckboxIconChecked from '../../../../packages/common/shared-ui/src/icons/CheckboxIconChecked';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ClearFormFieldIcon from '../../../../packages/common/shared-ui/src/icons/ClearFormFieldIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { LanguageMap } from '../../../../packages/common/utils/languageMap';

import IssueFileUpload from './IssueFileUpload';

import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom/model';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';
import 'tinymce/skins/content/default/content.min.css';

// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    position: 'static',
    width: '200px',
    color: '#41424E',
    lineHeight: '20px',
    transform: 'none',
    display: 'flex',
    flexShrink: 0,
    '& .MuiFormLabel-asterisk': {
      color: '#E03737',
    },
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
  },
}));
// Original class: textField
const StyledTextField = styled(TextField)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: 'auto',
    '&.MuiInputBase-multiline': {
      padding: 0,
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      fontSize: '12px',
      lineHeight: '20px',
      padding: '8px 16px',
      height: 'auto',
      color: '#212346',
      '&::placeholder': {
        color: '#7174AC',
        fontSize: '12px',
        lineHeight: '20px',
      },
    },
    '&.Mui-disabled': {
      '& .MuiInputBase-input.MuiOutlinedInput-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
    },
  },
}));
// Original class: booleanWrap
const StyledBooleanWrap = styled(RadioGroup)(theme => ({
  flexDirection: 'row !important',
}));
// Original class: booleanLabel
const StyledBooleanLabel = styled(FormControlLabel)(theme => ({
  '&.MuiFormControlLabel-root': {
    margin: 0,
  },
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '8px 65px 8px 16px',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    //width: '100%!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker)(theme => ({
  '&.MuiFormControl-root.MuiTextField-root': {
    height: 'auto',
    width: '116px',
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      height: 'auto',
      padding: 0,
      '&.Mui-disabled': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#E4E4EF',
        },
        '& .MuiInputAdornment-root': {
          '& .MuiSvgIcon-root path': {
            fill: '#E4E4EF',
          },
        },
      },
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      height: 'auto',
      padding: '8px 30px 8px 16px',
      textTransform: 'lowercase',
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input::placeholder': {
      textTransform: 'lowercase',
    },
  },

  '& .MuiInputAdornment-root': {
    position: 'absolute',
    right: '20px',
  },
}));
// Original class: tooltipForm
const StyledTooltipForm = styled(Popper)(theme => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#F1F2F8',
    borderRadius: '8px',
    padding: '4px',
    color: '#212346',
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    margin: 0,
    marginBottom: '0!important',
  },
}));
const IssueFormField = ({
  field,
  handleField,
  bulk,
  changeParentIssue,
  parentIssueOptions,
  modal,
  handleFocusField,
}) => {
  const getLocalString = useTranslation();
  const [parentIssue, setParentIssue] = useState('');
  const [singleField, setSingleField] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [language, setLanguage] = useState('ru');

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  useEffect(() => {
    if (field.fieldId === 'description' || field.fieldId === 'notes') {
      setSingleField(true);
    }
    if (field.fieldId === 'parentIssue') {
      setParentIssue({
        ...field,
        valueId: field.valueId,
        valueName: field.valueName,
      });
    }
  }, [field]);

  const handleNumberField = useCallback(
    (event, field) => {
      let reg = new RegExp(/^[0-9,.]*$/);
      field.fieldDefinition.fieldFormat === 'progressbar' &&
        (reg = new RegExp(/^[0-9]*$/));
      if (reg.test(event.target.value)) {
        handleField(event.target.value, field);
      }
    },
    [handleField],
  );
  const validateUrl = useCallback((value, field) => {
    const urlPattern = new RegExp(
      '^(https?:\\/\\/)?' + // validate protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
        '(\\#[-a-z\\d_]*)?$',
      'i',
    );
    value !== '' &&
      field === 'link' &&
      !urlPattern.test(value) &&
      setFieldError(true);
    value !== '' &&
      field === 'link' &&
      urlPattern.test(value) &&
      setFieldError(false);
  }, []);
  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
        flexWrap: 'nowrap',
        position: 'relative',
        marginTop: singleField ? 0 : '16px',
      }}
    >
      <FormControl
        required={field.fieldDefinition.isRequired}
        key={field.fieldId}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          alignItems: singleField ? 'flex-start' : 'center',
        }}
      >
        <StyledSelectLabel sx={{ marginTop: singleField ? '8px' : 0 }}>
          <Typography
            variant='h5'
            sx={{
              color: '#41424E',
            }}
          >
            {field.fieldName}
          </Typography>
        </StyledSelectLabel>
        {(field.fieldDefinition.fieldFormat === 'float' ||
          field.fieldDefinition.fieldFormat === 'progressbar' ||
          field.fieldDefinition.fieldFormat === 'int') && (
          <StyledTextField
            onFocus={event => handleFocusField(event, field)}
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            error={field.error}
            required={field.fieldDefinition.isRequired}
            variant='outlined'
            sx={{ width: 250 }}
            placeholder={getLocalString('labels', 'input')}
            //defaultValue={field.valueName ? field.valueName : ''}
            value={field.valueName ? field.valueName : ''}
            onChange={event => handleNumberField(event, field)}
            slotProps={{
              htmlInput: {
                maxLength: field.fieldDefinition.maxLength,
                minLength: field.fieldDefinition.minLength,
              },
            }}
          ></StyledTextField>
        )}
        {(field.fieldDefinition.fieldId === 'description' ||
          field.fieldDefinition.fieldFormat === 'text') && (
          <Grid
            container
            sx={{
              flexDirection: 'column',
            }}
          >
            <Editor
              init={{
                skin: false,
                content_css: false,
                height: 200,
                menubar: false,
                plugins:
                  field.fieldDefinition.fieldId === 'description'
                    ? 'image autolink'
                    : '',
                toolbar: 'formatselect | bold italic backcolor',
                link_default_target: '_blank',
                branding: false,
              }}
              value={field.valueName ? field.valueName : ''}
              onEditorChange={content => handleField(content, field)}
            />
          </Grid>
        )}
        {field.fieldDefinition.fieldId !== 'description' &&
          (field.fieldDefinition.fieldFormat === 'string' ||
            field.fieldDefinition.fieldFormat === 'link') && (
            <StyledTextField
              onBlur={event =>
                validateUrl(
                  event.target.value,
                  field.fieldDefinition.fieldFormat,
                )
              }
              disabled={field.deleted || field.fieldDefinition.isReadonly}
              error={field.error}
              multiline={singleField}
              rows={singleField ? 3 : ''}
              required={field.fieldDefinition.isRequired}
              sx={{ width: 353 }}
              variant='outlined'
              placeholder={getLocalString('labels', 'input')}
              value={field.valueName ? field.valueName : ''}
              onChange={event => handleField(event, field)}
            ></StyledTextField>
          )}
        {field.fieldDefinition.fieldFormat === 'bool' && (
          <StyledBooleanWrap
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            required={field.fieldDefinition.isRequired}
            onChange={(event, value) => handleField(value, field)}
            value={field.valueId}
          >
            <StyledBooleanLabel
              value={true}
              checked={field.valueId !== '' && field.valueId === 'true'}
              control={
                <Radio
                  disableRipple
                  icon={
                    field.error ? (
                      <CheckboxIcon
                        color={'#E03737'}
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    ) : (
                      <CheckboxIcon
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    )
                  }
                  checkedIcon={
                    <CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                  }
                />
              }
              label={
                <Typography variant='h5'>
                  {getLocalString('labels', 'yes')}
                </Typography>
              }
            />
            <StyledBooleanLabel
              value={false}
              checked={field.valueId !== '' && field.valueId === 'false'}
              control={
                <Radio
                  disableRipple
                  icon={
                    field.error ? (
                      <CheckboxIcon
                        color={'#E03737'}
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    ) : (
                      <CheckboxIcon
                        width='12'
                        height='12'
                        viewBox='0 0 12 12'
                      />
                    )
                  }
                  checkedIcon={
                    <CheckboxIconChecked
                      width='12'
                      height='12'
                      viewBox='0 0 12 12'
                    />
                  }
                />
              }
              label={
                <Typography variant='h5'>
                  {getLocalString('labels', 'no')}
                </Typography>
              }
            />
          </StyledBooleanWrap>
        )}
        {field.fieldId === 'parentIssue' && (
          <StyledFilterSelect
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            noOptionsText={getLocalString('labels', 'notFound')}
            filterOptions={x => x}
            required={field.fieldDefinition.isRequired}
            onChange={(event, newValue) => handleField(newValue, field)}
            disablePortal
            options={parentIssueOptions}
            sx={{ width: 253 }}
            disableClearable={bulk || !modal}
            renderOption={(props, option) => {
              return (
                <MenuItem
                  {...props}
                  selected={option.valueId === field.valueId}
                  key={option.valueId}
                  value={option.valueId + option.valueName}
                >
                  {option.valueName}
                </MenuItem>
              );
            }}
            value={parentIssue ? parentIssue : ''}
            getOptionLabel={option => option.valueName || ''}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={params => (
              <TextField
                {...params}
                error={field.error}
                onChange={ev => changeParentIssue(ev.target.value)}
                placeholder={getLocalString('labels', 'select')}
              />
            )}
            popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
            slots={{
              popper: props => (
                <Popper {...props} placement='bottom-start'></Popper>
              ),
            }}
          />
        )}
        {field.fieldDefinition.values && (
          <StyledFilterSelect
            noOptionsText={getLocalString('labels', 'notFound')}
            disabled={field.deleted || field.fieldDefinition.isReadonly}
            required={field.fieldDefinition.isRequired}
            onChange={(event, newValue) => handleField(newValue, field)}
            disablePortal
            options={field.fieldDefinition.values}
            sx={{ width: 253 }}
            disableClearable={bulk}
            renderOption={(props, option) => {
              return (
                <MenuItem
                  {...props}
                  key={option.valueId + option.valueName}
                  value={option.valueId}
                  selected={
                    field.valueId &&
                    option.valueId.toString() === field.valueId.toString()
                  }
                >
                  {option.valueName}
                </MenuItem>
              );
            }}
            defaultValue={field.fieldDefinition.values.find(
              item => item.isChecked,
            )}
            //value={field.fieldDefinition.values.find(item => item.isChecked)}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            getOptionLabel={option => option.valueName || ''}
            renderInput={params => (
              <TextField
                {...params}
                error={field.error}
                placeholder={getLocalString('labels', 'select')}
              />
            )}
            popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
            slots={{
              popper: props => (
                <Popper {...props} placement='bottom-start'></Popper>
              ),
            }}
          />
        )}
        {field.fieldDefinition.fieldFormat === 'date' && (
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale={language}
          >
            <StyledDatePicker
              required={field.fieldDefinition.isRequired}
              disabled={field.deleted || field.fieldDefinition.isReadonly}
              format={'DD.MM.YYYY'}
              slots={{
                openPickerIcon: ({ ...props }) => (
                  <CalendarFormIcon {...props} />
                ),
                leftArrowIcon: () => (
                  <ChevronIcon
                    direction='left'
                    viewBox='0 0 24 24'
                    width={24}
                    height={24}
                  />
                ),
                rightArrowIcon: () => (
                  <ChevronIcon
                    direction='right'
                    viewBox='0 0 24 24'
                    width={24}
                    height={24}
                  />
                ),
                rightArrowButton: componentProps => (
                  <IconButton disableRipple {...componentProps}>
                    {componentProps.children}
                  </IconButton>
                ),
                leftArrowButton: componentProps => (
                  <IconButton disableRipple {...componentProps}>
                    {componentProps.children}
                  </IconButton>
                ),
              }}
              slotProps={{
                textField: {
                  inputProps: { placeholder: 'dd.mm.yyyy' },
                },
                field: {
                  placeholder: 'dd.mm.yyyy',
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  error={field.error}
                  placeholder={getLocalString('labels', 'input')}
                />
              )}
              value={
                field.valueName && field.valueName !== ''
                  ? dayjs(field.valueName)
                  : null
              }
              onChange={value => handleField(value, field)}
              //autoFocus={true}
            />
          </LocalizationProvider>
        )}
        {field.fieldDefinition.fieldFormat === 'attachment' && (
          <IssueFileUpload
            handleUpload={value => handleField(value, field)}
            single={true}
          ></IssueFileUpload>
        )}
      </FormControl>
      {bulk && !field.fieldDefinition.isRequired && (
        <Tooltip
          placement='top-start'
          title={getLocalString('labels', 'clear')}
          slots={{
            popper: StyledTooltipForm,
          }}
        >
          <IconButton
            onClick={value => handleField(value, field, true)}
            disableRipple
            sx={{ padding: 0, marginLeft: '2px' }}
          >
            <ClearFormFieldIcon color={field.deleted ? '#E4E4EF' : '#7174AC'} />
          </IconButton>
        </Tooltip>
      )}
      {fieldError && (
        <Box sx={{ position: 'absolute', bottom: '-17px', left: '205px' }}>
          <Typography
            variant='h5'
            sx={{
              color: '#E03737',
            }}
          >
            {getLocalString('errors', 'urlError')}
          </Typography>
        </Box>
      )}
    </Grid>
  );
};

export default IssueFormField;
